import { Button, Card, Divider, Flex, Link, Text, TextField } from '@servicetitan/anvil2';
import { useEffect, useState } from 'react';
import CloseIcon from '@servicetitan/anvil2/assets/icons/material/round/close.svg';
import { LoadingScreen } from '@servicetitan/financing-utils-shared';
import { App } from '@servicetitan/web-feature-app-service';
import { AffirmPlanRow } from './AffirmPlanRow';
import { useAffirmPaymentOptions } from './useAffirmPaymentOptions';
import { cardSection, header, planCard, screen } from './AffirmPaymentOptionsScreen.module.css';

const handleClose = () => {
    App.exitApp().catch(() => {
        window.close();
    });
};

export const AffirmPaymentOptionsScreen = () => {
    const { localAmount, handleAmountChange, plans, isLoading } = useAffirmPaymentOptions();
    const [rawInput, setRawInput] = useState(localAmount > 0 ? String(localAmount) : '');

    useEffect(() => {
        setRawInput(localAmount > 0 ? String(localAmount) : '');
    }, [localAmount]);

    return (
        <div className={screen}>
            <div className={header}>
                <Button
                    icon={CloseIcon}
                    onClick={handleClose}
                    appearance="ghost"
                    size="large"
                    aria-label="Close"
                    style={{ position: 'absolute', top: 0, right: 0 }}
                />
                <Flex direction="column" gap="3">
                    <Text variant="headline" el="h1" size="medium">
                        Affirm pre-qualification is only available for customers in online
                        estimates.
                    </Text>
                    <Text>
                        To share the link, click <strong>Share</strong> icon in the top right corner
                        when presenting a proposal or viewing an individual estimate. From there,
                        you can email or text it directly.
                    </Text>
                </Flex>
            </div>

            <Flex direction="column" gap="4" style={{ marginTop: '2rem' }}>
                <Text variant="headline" el="h2" size="medium">
                    Explore Affirm payment options
                </Text>

                <Card flexDirection="column" className={planCard} padding="0">
                    <div className={cardSection}>
                        <TextField
                            label="Total Estimated Cost"
                            type="text"
                            prefix="$"
                            inputMode="decimal"
                            value={rawInput}
                            maxLength={6}
                            onChange={event => {
                                const clean = event.target.value
                                    .replace(/[^\d.]/g, '')
                                    .replace(/^(\d*\.?\d*).*$/, '$1');
                                setRawInput(clean);
                                const amount = Number(clean);
                                if (amount > 0 && !isNaN(amount)) {
                                    handleAmountChange(amount);
                                }
                            }}
                        />
                    </div>

                    {isLoading ? (
                        <div className={cardSection}>
                            <LoadingScreen message="Loading payment options..." />
                        </div>
                    ) : (
                        plans.map((plan, index) => (
                            <div key={plan.planNumber ?? index}>
                                {index > 0 && <Divider spacing="0" />}
                                <div className={cardSection}>
                                    <AffirmPlanRow plan={plan} />
                                </div>
                            </div>
                        ))
                    )}
                </Card>

                <Flex direction="column" gap="3">
                    <Text variant="body" size="small" subdued>
                        Subject to eligibility. For example, a $1,000 purchase might cost $90.26/mo
                        over 12 months at 15% APR. See lending terms at{' '}
                        <Link href="https://www.affirm.com/disclosures" target="_blank">
                            affirm.com/disclosures
                        </Link>
                        . Affirm and its lending partners do business in accordance with federal
                        Fair Lending laws.
                    </Text>
                    <Text variant="body" size="small" subdued>
                        Submitting a pre-qualification application will not affect the
                        customer&apos;s credit score.
                    </Text>
                </Flex>
            </Flex>
        </div>
    );
};
